import { useEffect } from "react";

import { useCurrentUser } from "@/lib/hooks/auth";
import { useCurrentBranding, useEffectiveBranding } from "@/lib/hooks/branding";
import { DEFAULT_THEME } from "@/lib/schemas/branding";
import { applyBrandFavicon, applyBrandTheme, resetBrandTheme } from "@/lib/theme-apply";

/**
 * Apply brand theme to runtime panel chrome (Phase 10B + 11.5 fallback).
 *
 * Mounts once at app root. Subscribes a `/branding/current` (host-aware,
 * 5min staleTime). When data arrives, sets CSS custom properties on
 * `document.documentElement` that override the Dinova default in
 * `globals.css :root`. Tailwind v3.4 / shadcn use `hsl(var(--primary))` etc.,
 * so the override automatically cascades to the entire chrome.
 *
 * Phase 11.5: prima di Phase 11.5, se `/branding/current` falliva (network
 * error, BE offline, host non-routed) la pagina restava sui CSS fallback
 * di globals.css → confusione "default non applicato". Ora applyBrandTheme
 * usa `DEFAULT_THEME` come fallback immediato + sostituisce con il backend
 * default appena la query risponde.
 *
 * Does NOT render any visible markup. Returns `children` unchanged.
 *
 * Cleanup: on unmount, resetBrandTheme() restores `globals.css` defaults.
 */
export function BrandingThemeProvider({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element {
  const { data: hostBranding } = useCurrentBranding();
  // Backlog 3.7: once authenticated, the per-user brand override
  // (app_users.brand_id) takes precedence over the host-based brand, so a
  // delegated reseller on the main panel sees their own brand.
  const me = useCurrentUser();
  const { data: effectiveBranding } = useEffectiveBranding(Boolean(me.data));
  const data = effectiveBranding ?? hostBranding;

  // Apply DEFAULT_THEME al mount (pre-fetch), aggiorna al data arrive.
  // Pre-Phase 11.5: solo data?.theme path → fallback CSS variables of
  // globals.css mostrato finche' la fetch /branding/current non risponde.
  useEffect(() => {
    const theme = data?.theme ?? DEFAULT_THEME;
    applyBrandTheme(theme);
    // applyBrandTheme is dark-mode-aware (it skips the brand bg/fg surfaces in
    // dark mode — see theme-apply.ts). The dark/light toggle only flips the
    // `.dark` class on <html> (lib/theme.ts), with no subscribable store, so
    // observe that class and re-apply whenever it changes. Filtered to the
    // `class` attribute → applyBrandTheme only mutates `style`, so no loop.
    const root = document.documentElement;
    const observer = new MutationObserver(() => applyBrandTheme(theme));
    observer.observe(root, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, [data?.theme]);

  // Phase 11.8: rewrite document `<link rel="icon">` to brand favicon when
  // `/branding/current` ritorna un favicon_url. Null/missing → restore
  // `/favicon.svg` default. La query e' host-aware quindi il favicon
  // segue automaticamente il match brand_domains/primary_domain/panel_default
  // del resolver multi-stage (Phase 11.7).
  useEffect(() => {
    applyBrandFavicon(data?.asset_urls?.favicon_url ?? null);
  }, [data?.asset_urls?.favicon_url]);

  // Final cleanup when the provider itself unmounts.
  useEffect(() => {
    return () => {
      resetBrandTheme();
      applyBrandFavicon(null); // restore /favicon.svg
    };
  }, []);

  return <>{children}</>;
}
